import React from "react";
import { Button } from "@progress/kendo-react-buttons";
import type { GridFilterCellProps } from "@progress/kendo-react-grid";

/**
 * Compiles a user-supplied regex. Returns `null` for an empty value (no filtering)
 * and throws nothing: invalid input is reported through {@link RegexFilterState}.
 */
export const compileRegexFilter = (value?: string | null): RegExp | null => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return null;
  }

  try {
    return new RegExp(trimmed, "i");
  } catch {
    return null;
  }
};

export const isRegexFilterValid = (value: string): boolean => {
  const trimmed = value.trim();
  if (!trimmed) {
    return true;
  }

  try {
    new RegExp(trimmed);
    return true;
  } catch {
    return false;
  }
};

export type RegexFilterState = {
  /**
  Current text in the box, including invalid intermediate input.
  */
  value: string;
  setValue: (next: string) => void;
  /**
  Compiled regex, or `null` when empty or invalid.
  */
  regex: RegExp | null;
  isValid: boolean;
  /**
  True when the user has diverged from the project default.
  */
  isOverridden: boolean;
  resetToProjectDefault: () => void;
};

/**
 * Editable regex filter seeded from a project-level default.
 *
 * The project default is the starting point; the user can change it freely for the
 * current session without mutating project configuration. Re-seeding happens only
 * when the project default itself changes.
 */
export const useRegexFilter = (projectDefaultRegex?: string | null): RegexFilterState => {
  const normalizedProjectDefault = (projectDefaultRegex ?? "").trim();
  const [value, setValue] = React.useState(normalizedProjectDefault);
  const lastProjectDefaultRef = React.useRef(normalizedProjectDefault);

  React.useEffect(() => {
    if (lastProjectDefaultRef.current === normalizedProjectDefault) {
      return;
    }

    lastProjectDefaultRef.current = normalizedProjectDefault;
    setValue(normalizedProjectDefault);
  }, [normalizedProjectDefault]);

  const isValid = React.useMemo(() => isRegexFilterValid(value), [value]);
  const regex = React.useMemo(() => (isValid ? compileRegexFilter(value) : null), [isValid, value]);
  const resetToProjectDefault = React.useCallback(() => setValue(normalizedProjectDefault), [normalizedProjectDefault]);

  return {
    value,
    setValue,
    regex,
    isValid,
    isOverridden: value.trim() !== normalizedProjectDefault,
    resetToProjectDefault,
  };
};

export type RegexFilterBarProps = {
  label: string;
  state: RegexFilterState;
  placeholder?: string;
  disabled?: boolean;
  /**
  Shown next to the box, e.g. "12 of 340 shown".
  */
  matchSummary?: string;
};

/**
 * Renders the editable text filter for an explorer. Kept deliberately compact so it
 * can sit inside an existing toolbar row.
 */
export const RegexFilterBar: React.FC<RegexFilterBarProps> = ({ label, state, placeholder = "Regular expression, e.g. ^QC_", disabled = false, matchSummary }) => {
  const inputId = React.useId();

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap",
        padding: "6px 8px",
      }}
    >
      <label htmlFor={inputId} style={{ fontSize: 12, color: "#475569", whiteSpace: "nowrap" }}>
        {label}
      </label>
      <input
        id={inputId}
        type="search"
        value={state.value}
        disabled={disabled}
        placeholder={placeholder}
        aria-invalid={!state.isValid}
        onChange={(event) => state.setValue(event.target.value)}
        style={{
          flex: "1 1 220px",
          minWidth: 180,
          padding: "4px 8px",
          borderRadius: 6,
          border: `1px solid ${state.isValid ? "#cbd5e1" : "#ef4444"}`,
          backgroundColor: state.isValid ? undefined : "#fff7ed",
        }}
      />
      {state.isOverridden && (
        <Button type="button" size="small" fillMode="outline" disabled={disabled} title="Reset this filter to the project default" onClick={state.resetToProjectDefault}>
          Reset
        </Button>
      )}
      {!state.isValid && <span style={{ color: "#b91c1c", fontSize: 11 }}>Not a valid regular expression.</span>}
      {state.isValid && matchSummary && <span style={{ color: "#64748b", fontSize: 11 }}>{matchSummary}</span>}
    </div>
  );
};

export default RegexFilterBar;

/**
 * Supplies the active regex filter to grid filter cells.
 *
 * Kendo renders filter cells itself and does not re-render them when the grid's
 * data changes, so a cell reading through a ref goes stale - notably on the first
 * render, before the project default has resolved. Context updates reach consumers
 * regardless of intervening memoization, so the cell always reflects live state.
 */
export const RegexFilterContext = React.createContext<RegexFilterState | null>(null);

export type RegexFilterProviderProps = {
  state: RegexFilterState;
  children: React.ReactNode;
};

export const RegexFilterProvider: React.FC<RegexFilterProviderProps> = ({ state, children }) => <RegexFilterContext.Provider value={state}>{children}</RegexFilterContext.Provider>;

/**
 * Kendo's own column filter uses `contains` semantics, so a regex typed there would
 * be matched literally. This cell bypasses Kendo's filter descriptor entirely and
 * drives the shared regex state instead, which the grid's data source already
 * applies.
 *
 * The component identity is stable so typing does not remount the input and drop
 * focus; live state arrives through {@link RegexFilterContext}.
 */
export const RegexFilterCell: React.FC<GridFilterCellProps> = () => {
  const state = React.useContext(RegexFilterContext);

  if (!state) {
    return null;
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      <input
        type="text"
        className="k-input k-textbox"
        value={state.value}
        placeholder="Regex, e.g. ^QC_"
        aria-label="Regular expression filter"
        aria-invalid={!state.isValid}
        onChange={(event) => state.setValue(event.target.value)}
        style={{
          width: "100%",
          minWidth: 0,
          padding: "4px 8px",
          borderRadius: 4,
          border: `1px solid ${state.isValid ? "#cbd5e1" : "#ef4444"}`,
          backgroundColor: state.isValid ? undefined : "#fff7ed",
        }}
      />
      {state.isOverridden && (
        <Button type="button" size="small" fillMode="flat" title="Reset to the project default" aria-label="Reset regex filter to the project default" onClick={() => state.resetToProjectDefault()}>
          ×
        </Button>
      )}
    </div>
  );
};
